import React, { useState, useEffect } from 'react';
import { Trophy, Plus, LogIn as LoginIcon, Ticket, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import GameRow from './GameRow';
import KindChoiceModal from './KindChoiceModal';
import CreateGameWizard from './CreateGameWizard';
import JoinGameModal from './JoinGameModal';
import CreateMiniLeagueWizard from '../miniLeagues/CreateMiniLeagueWizard';
import JoinLeagueModal from '../miniLeagues/JoinLeagueModal';
import LeagueGroup from './LeagueGroup';
import { groupGames, partitionArchived } from './groupGames';
import { KIND_LIST } from './gameKinds';
import * as gamesApi from '../../api/games';
import * as miniLeaguesApi from '../../api/miniLeagues';
import * as tournamentsApi from '../../api/tournaments';

/**
 * Everything a player is in, on one page.
 *
 * Leagues and games used to be two tabs, which meant the same league game was drawn
 * twice - once as a row inside its league's card, once as a card of its own carrying
 * the league's name - and a player had to know which of the two words their thing was
 * filed under before they could go and look at it. That is a distinction the data model
 * cares about, not one the player does.
 *
 * So games are the objects and leagues are the grouping. Which kind a game is never
 * needs saying: a game under a league's name is a league game, and a game under
 * One-off games is not.
 */

// Home-page action ids, all four of which land here. `join-game` also arrives
// straight after registration, carrying the code the new player typed on the way in -
// see `initialJoinCode`.
const ACTION_FLOWS = {
  'create-game': 'create-game',
  'join-game': 'join-game',
  'create-league': 'create-league',
  'join-league': 'join-league',
};

function MyGamesPage({ currentUser, onViewGame, onViewLeague, initialAction, initialJoinCode, autoJoin = false, onActionConsumed }) {
  const [games, setGames] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFlow, setOpenFlow] = useState(null);
  /**
   * The code a brand-new player typed on the register form, held from the moment the
   * flow opens until the box closes.
   *
   * Taken into local state rather than read straight off `initialJoinCode`, because the
   * effect below consumes the action in the same pass that opens the flow - so by the
   * time the modal mounts, the prop it would have read is already back to empty.
   */
  const [joinCodeForFlow, setJoinCodeForFlow] = useState('');
  // Whether that code came from a join link, kept locally for the same reason (item 89).
  const [autoJoinForFlow, setAutoJoinForFlow] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    loadGames();
    loadLeagues();
    loadTournaments();
  }, [currentUser]);

  // Open whatever the home page asked for, then clear it so returning to this
  // page later doesn't reopen the same flow.
  useEffect(() => {
    if (!initialAction) return;
    const flow = ACTION_FLOWS[initialAction];
    if (flow) {
      setOpenFlow(flow);
      setJoinCodeForFlow(initialJoinCode || '');
      setAutoJoinForFlow(Boolean(autoJoin));
    }
    if (onActionConsumed) onActionConsumed();
  }, [initialAction, initialJoinCode, autoJoin, onActionConsumed]);

  const loadGames = async () => {
    try {
      setGames(await gamesApi.getMyGames());
    } catch (err) {
      console.error('Error loading games:', err);
    } finally {
      setLoading(false);
    }
  };

  // Failing here costs the league headings and nothing else - groupGames rebuilds a
  // heading from the game itself rather than dropping games it cannot file.
  const loadLeagues = async () => {
    try {
      setLeagues(await miniLeaguesApi.getMyLeagues());
    } catch (err) {
      console.error('Error loading leagues:', err);
    }
  };

  const loadTournaments = async () => {
    try {
      setTournaments(await tournamentsApi.getTournaments());
    } catch (err) {
      console.error('Error loading tournaments:', err);
    }
  };

  const closeFlow = () => {
    setOpenFlow(null);
    setJoinCodeForFlow('');
    setAutoJoinForFlow(false);
  };

  // A join link lands on what it joined; a typed code stays on this list, as it always has.
  const afterJoining = (destination) => {
    closeFlowAndReload();
    if (destination?.gameId) {
      onViewGame(destination.gameId);
    } else if (destination?.leagueId) {
      onViewLeague(destination.leagueId);
    }
  };

  const closeFlowAndReload = () => {
    closeFlow();
    loadGames();
    loadLeagues();
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleRemovePlayer = async (gameId, playerId, playerName) => {
    if (!window.confirm(`Remove ${playerName} from this game?`)) return;
    try {
      await gamesApi.removePlayer(gameId, playerId);
      loadGames();
    } catch (err) {
      if (err.name === 'ApiError') {
        alert(err.body?.error || 'Failed to remove player');
      } else {
        alert('Error removing player');
      }
    }
  };

  const { current, archived } = partitionArchived(games, leagues);
  const shown = showArchived ? { games, leagues } : current;
  const { leagueGroups, standalone } = groupGames(shown.games, shown.leagues);
  const archivedCount = archived.leagues.length + archived.games.length;
  const nothingYet = games.length === 0 && leagues.length === 0;

  const rowProps = {
    currentUser,
    copiedCode,
    onCopyCode: copyCode,
    onRemovePlayer: handleRemovePlayer,
    onViewGame,
  };

  if (loading) {
    return (
      <div className="page-container fade-in">
        <div className="content-card">
          <div className="loading-spinner">Loading your games...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container fade-in">
      <div className="content-card">
        <div className="card-header">
          <Trophy className="header-icon" size={48} />
          <h1>My Games</h1>
          <p>Your Mini Leagues and One-off Games</p>
        </div>

        {/* Joining leads, creating follows. Most players are handed a code by somebody
            else; creating is a thing one person per group ever does. */}
        <div className="action-buttons">
          <button className="primary-btn" onClick={() => setOpenFlow('join-game')}>
            <LoginIcon size={20} />
            Join with a Code
          </button>
          <button className="secondary-btn" onClick={() => setOpenFlow('create-choice')}>
            <Plus size={20} />
            New
          </button>
        </div>

        {nothingYet ? (
          <div className="empty-state kind-choice">
            {KIND_LIST.map((kind) => (
              <button
                key={kind.id}
                className="kind-choice-option"
                onClick={() => setOpenFlow(kind.createFlow)}
              >
                <span className="kind-choice-label">{kind.label}</span>
                <span className="kind-choice-blurb">{kind.blurb}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="list-columns">
            {/* Two labelled sections rather than one run of groups. "Mini Leagues" and
                "One-off Games" are the words this audience already has from Fantasy
                Premier League and Super 6 - the app should not teach them new ones for
                the same two things. */}
            {leagueGroups.length > 0 && (
              <section className="list-section">
                <h2 className="section-label">
                  <Shield size={18} />
                  Mini Leagues
                </h2>
                <div className="games-grid">
                  {leagueGroups.map(({ league, games: leagueGames }) => (
                    <LeagueGroup
                      key={league.id}
                      league={league}
                      games={leagueGames}
                      currentUser={currentUser}
                      copiedCode={copiedCode}
                      onCopyCode={copyCode}
                      onRemovePlayer={handleRemovePlayer}
                      onViewGame={onViewGame}
                      onViewLeague={onViewLeague}
                    />
                  ))}
                </div>
              </section>
            )}

            {standalone.length > 0 && (
              <section className="list-section">
                <h2 className="section-label">
                  <Ticket size={18} />
                  One-off Games
                </h2>
                <div className="games-grid">
                  {/* One group, not one per game - a one-off has no heading of its own
                      to sit under, so the section label is doing that job. */}
                  <div className="game-group standalone-group">
                    {standalone.map((game) => <GameRow key={game.id} game={game} {...rowProps} />)}
                  </div>
                </div>
              </section>
            )}
          </div>
        )}

        {/* Archived is the API's rule - finished, and finished a while ago. A game that
            ended on Saturday is still in the list above all week, which is the point. */}
        {archivedCount > 0 && (
          <button className="archived-toggle" onClick={() => setShowArchived((s) => !s)}>
            {showArchived ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
          </button>
        )}
      </div>

      {openFlow === 'create-choice' && (
        <KindChoiceModal onChoose={setOpenFlow} onClose={closeFlow} />
      )}

      {openFlow === 'create-game' && (
        <CreateGameWizard tournaments={tournaments} onClose={closeFlow} onCreated={closeFlowAndReload} />
      )}

      {openFlow === 'join-game' && (
        <JoinGameModal
          onClose={closeFlow}
          onJoined={afterJoining}
          initialCode={joinCodeForFlow}
          autoJoin={autoJoinForFlow}
        />
      )}

      {openFlow === 'create-league' && (
        <CreateMiniLeagueWizard tournaments={tournaments} onClose={closeFlow} onCreated={closeFlowAndReload} />
      )}

      {openFlow === 'join-league' && (
        <JoinLeagueModal onClose={closeFlow} onJoined={closeFlowAndReload} />
      )}
    </div>
  );
}

export default MyGamesPage;
